import { useState, useEffect, useCallback, useRef } from 'react';
import { differenceInSeconds } from 'date-fns';
import confetti from 'canvas-confetti';
import { sendNotification } from '../utils/notifications';
import { PHASES } from '../utils/data';

const STORAGE_KEY = 'ketomachine_data_v3';

export const useFasting = () => {
  const [state, setState] = useState(() => {
    const defaultState = {
      isFasting: false,
      startTime: null,
      targetHours: 16,
      protocolId: '16-8',
      history: [],
      weightLogs: [],
      measurements: [],
      photos: [],
      streak: 0,
      longestFast: 0,
      lastFastEnd: null,
      settings: {
        units: 'metric',
        notificationsEnabled: false,
        remindPhases: true,
        remindGoal: true
      }
    };

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaultState, ...JSON.parse(saved) } : defaultState;
    } catch (e) {
      console.error('Error loading saved data:', e);
      return defaultState;
    }
  });

  const [elapsed, setElapsed] = useState(0);
  const lastPhaseRef = useRef(null);
  const goalReachedRef = useRef(false);

  // Save to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Error saving data:', e);
    }
  }, [state]);

  // Notification Logic
  useEffect(() => {
    if (!state.isFasting || !state.settings.notificationsEnabled) {
      lastPhaseRef.current = null;
      goalReachedRef.current = false;
      return;
    }

    const elapsedHours = elapsed / 3600;

    // Check Phase Transitions
    if (state.settings.remindPhases) {
      const currentPhase = PHASES.find(p => elapsedHours >= p.start && elapsedHours < p.end);
      if (currentPhase && lastPhaseRef.current !== currentPhase.title) {
        if (lastPhaseRef.current !== null) {
          sendNotification(
            `Stage ${PHASES.indexOf(currentPhase) + 1}: ${currentPhase.title}`,
            currentPhase.desc
          );
        }
        lastPhaseRef.current = currentPhase.title;
      }
    }

    // Check Goal Completion
    if (state.settings.remindGoal && !goalReachedRef.current && elapsedHours >= state.targetHours) {
      sendNotification(
        "Goal Reached! 🏆",
        `Congratulations! You've completed your ${state.targetHours}h fast.`
      );
      goalReachedRef.current = true;
    }
  }, [elapsed, state.isFasting, state.settings.notificationsEnabled, state.settings.remindPhases, state.settings.remindGoal, state.targetHours]);

  // Timer update
  useEffect(() => {
    let interval;
    if (state.isFasting && state.startTime) {
      const update = () => {
        const now = new Date();
        const start = new Date(state.startTime);
        setElapsed(Math.max(0, differenceInSeconds(now, start)));
      };

      update();
      interval = setInterval(update, 1000);
    } else {
      setElapsed(0);
    }

    return () => clearInterval(interval);
  }, [state.isFasting, state.startTime]);

  const toggleUnits = useCallback(() => {
    setState(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        units: prev.settings?.units === 'metric' ? 'imperial' : 'metric'
      }
    }));
  }, []);

  const updateSettings = useCallback((newSettings) => {
    setState(prev => ({
      ...prev,
      settings: { ...prev.settings, ...newSettings }
    }));
  }, []);

  const startFast = (protocolId, hours) => {
    lastPhaseRef.current = null;
    goalReachedRef.current = false;
    setState(prev => ({
      ...prev,
      isFasting: true,
      startTime: new Date().toISOString(),
      targetHours: hours,
      protocolId
    }));
  };

  const endFast = () => {
    if (!state.startTime) return;

    const now = new Date();
    const start = new Date(state.startTime);
    const durationHours = differenceInSeconds(now, start) / 3600;

    if (durationHours >= state.targetHours) {
      confetti({
        particleCount: 150,
        spread: 90,
        origin: { y: 0.6 }
      });
    }

    const newHistoryEntry = {
      id: Date.now(),
      start: state.startTime,
      end: now.toISOString(),
      duration: durationHours,
      target: state.targetHours,
      protocol: state.protocolId
    };

    const newHistory = [newHistoryEntry, ...(state.history || [])];

    let newStreak = state.streak || 0;
    const hoursSinceLast = state.lastFastEnd
      ? differenceInSeconds(now, new Date(state.lastFastEnd)) / 3600
      : 999;

    if (durationHours >= 12) {
      if (hoursSinceLast < 36 && hoursSinceLast > 0) newStreak += 1;
      else newStreak = 1;
    }

    setState(prev => ({
      ...prev,
      isFasting: false,
      startTime: null,
      history: newHistory,
      streak: newStreak,
      longestFast: Math.max(prev.longestFast || 0, durationHours),
      lastFastEnd: now.toISOString()
    }));
  };

  const addWeight = (weight) => {
    const val = parseFloat(weight);
    if (isNaN(val)) return;

    const newLog = {
      date: new Date().toISOString(),
      value: val
    };

    setState(prev => ({
      ...prev,
      weightLogs: [...(prev.weightLogs || []), newLog]
    }));
  };

  const addMeasurements = (data) => {
    const newEntry = {
      date: new Date().toISOString(),
      ...data
    };

    setState(prev => ({
      ...prev,
      measurements: [...(prev.measurements || []), newEntry]
    }));
  };

  const addPhoto = (photoUrl, type) => {
    const newPhoto = {
      id: Date.now(),
      date: new Date().toISOString(),
      url: photoUrl,
      type
    };

    setState(prev => ({
      ...prev,
      photos: [...(prev.photos || []), newPhoto]
    }));
  };

  const cancelFast = () => setState(prev => ({ ...prev, isFasting: false, startTime: null }));

  return {
    ...state,
    elapsed,
    startFast,
    endFast,
    cancelFast,
    addWeight,
    addMeasurements,
    addPhoto,
    toggleUnits,
    updateSettings
  };
};